/**
 * QuickActions — Super App Grid for the active dashboard
 *
 * Mirrors the sidebar's app rail — same Phosphor icons, same liquid glass
 * color scheme. Responsive grid with descriptive labels.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  navigationConfig,
  isDivider,
  type NavigationItem,
} from "@/lib/config/navigationConfig";
import { cn } from "@/lib/utils";
import { useAppAccess } from "@/hooks/useAppAccess";
import { CARD } from "@/config/design-tokens";
import { HugeiconsIcon } from "@hugeicons/react";
import { railHugeIcon } from "@/components/layout/railAppIcons";

// Phosphor icons — only DotsNine used for "see more" button
import { DotsNine } from "@phosphor-icons/react";

// Same utility app exclusion as SlideInSidebar
const UTILITY_APPS = ["แอป", "ทั้งหมด", "หน้าหลัก"];

// Descriptive labels for the dashboard grid
const DESCRIPTIVE_LABELS: Record<string, string> = {
  "หน้าหลัก": "แดชบอร์ด",
  "ธุรกิจ": "จัดการธุรกิจ\nทั้งหมด",
  "CRM": "ระบบ CRM\nฝ่ายขาย",
  "กล่องข้อความ": "รวมแชทลูกค้า\nจากทุกช่องทาง",
  "รายงาน": "Business\nIntelligence",
  "สร้างเว็บ": "จัดการ\nเว็บไซต์",
  "อีเมล": "อีเมล\nแคมเปญ",
  "โน้ต": "บันทึก\nและจดโน้ต",
};

function getAppsFromConfig(): (NavigationItem & { href: string })[] {
  let foundAppsDivider = false;
  const apps: (NavigationItem & { href: string })[] = [];

  for (const item of navigationConfig) {
    if (isDivider(item) && item.label === "Apps") {
      foundAppsDivider = true;
      continue;
    }
    if (foundAppsDivider && !isDivider(item) && !UTILITY_APPS.includes(item.name)) {
      const href = item.href ?? item.subMenu?.[0]?.items?.[0]?.href;
      if (href) apps.push({ ...item, href });
    }
  }

  return apps;
}

function AppIcon({ app }: { app: NavigationItem }) {
  // Desk-style monochrome line icon — matches the left app rail (ErpSidebarAppRail):
  // #505A62 idle stroke, transparent tile, soft gray hover highlight. Render the
  // SAME Hugeicons glyph the rail uses (shared railHugeIcon map, keyed by href),
  // falling back to the lucide app.icon for any app the rail hasn't mapped yet.
  const hugeIcon = railHugeIcon(app);
  const Icon = app.icon;
  return (
    <div className="w-11 h-11 flex items-center justify-center rounded-[12px] transition-colors duration-150 group-hover:bg-[#F0F1F2] dark:group-hover:bg-[#242a30]">
      {hugeIcon ? (
        <HugeiconsIcon
          icon={hugeIcon}
          size={24}
          strokeWidth={1.5}
          className="text-[#505A62] dark:text-white"
        />
      ) : (
        <Icon className="w-6 h-6 text-[#505A62] dark:text-white" strokeWidth={1.5} />
      )}
    </div>
  );
}

const MOBILE_VISIBLE = 7; // 4 + 3, 8th slot = "see more"

function AppGridItem({ app }: { app: NavigationItem & { href: string } }) {
  return (
    <Link
      key={app.name}
      to={app.href!}
      className="group flex flex-col items-center"
    >
      <div>
        <div
          className={cn(
            "transition-transform duration-200",
            "group-hover:scale-105",
            "group-active:scale-95"
          )}
        >
          <AppIcon app={app} />
        </div>
      </div>
      <span className="mt-2.5 text-[11px] font-medium text-muted-foreground text-center leading-tight w-full whitespace-pre-line">
        {DESCRIPTIVE_LABELS[app.name] ?? app.name}
      </span>
    </Link>
  );
}

export function QuickActions({ bare = false }: { bare?: boolean } = {}) {
  const allApps = getAppsFromConfig();
  const { hasAccess, isInstalled } = useAppAccess();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const apps = allApps.filter(
    (app) => !app.appAccessKey || hasAccess(app.appAccessKey)
  ).filter(
    // Opt-in only (Sheets): every other app keeps the rule above unchanged.
    (app) => !app.installGatedEverywhere || !app.appRegistryKey || isInstalled(app.appRegistryKey)
  );

  if (apps.length === 0) return null;

  // Desktop: show all apps in card
  if (!bare) {
    return (
      <div className={cn(CARD, "p-6 shadow-none")}>
        <h3 className="text-sm font-semibold text-foreground mb-5">แอปของคุณ</h3>
        <div className="grid grid-cols-4 lg:grid-cols-3 gap-y-6 gap-x-3">
          {apps.map((app) => (
            <AppGridItem key={app.name} app={app} />
          ))}
        </div>
      </div>
    );
  }

  // Mobile: 2 rows max, last slot = "see more"
  const needsOverflow = apps.length > MOBILE_VISIBLE;
  const visibleApps = needsOverflow ? apps.slice(0, MOBILE_VISIBLE) : apps;
  const overflowApps = needsOverflow ? apps.slice(MOBILE_VISIBLE) : [];

  return (
    <>
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-5">แอปของคุณ</h3>
        <div className="grid grid-cols-4 gap-y-6 gap-x-3">
          {visibleApps.map((app) => (
            <AppGridItem key={app.name} app={app} />
          ))}
          {needsOverflow && (
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="group flex flex-col items-center"
            >
              <div
                className={cn(
                  "w-11 h-11 flex items-center justify-center rounded-sm",
                  "bg-gray-100 dark:bg-gray-800",
                  "transition-all duration-200",
                  "group-hover:scale-105 group-active:scale-95"
                )}
              >
                <DotsNine size={26} weight="fill" className="text-gray-500 dark:text-gray-400" />
              </div>
              <span className="mt-2.5 text-[11px] font-medium text-muted-foreground text-center leading-tight w-full">
                ดูเพิ่มเติม
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Bottom drawer for overflow apps */}
      <AnimatePresence>
        {drawerOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="fixed inset-0 z-50 bg-black/30"
              onClick={() => setDrawerOpen(false)}
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{
                type: "tween",
                duration: 0.4,
                ease: [0.32, 0.72, 0, 1],
              }}
              className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-950 rounded-t-2xl shadow-2xl"
              style={{ height: "70vh" }}
            >
              {/* Drag handle */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-700" />
              </div>

              <div className="px-5 pb-2">
                <h3 className="text-sm font-semibold text-foreground">แอปทั้งหมด</h3>
              </div>

              <div className="px-5 pb-8 overflow-y-auto" style={{ maxHeight: "calc(70vh - 60px)" }}>
                <div className="grid grid-cols-4 gap-y-6 gap-x-3">
                  {overflowApps.map((app) => (
                    <AppGridItem key={app.name} app={app} />
                  ))}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
